import type { FrontendApi, SuccessfulNativeLogin } from '@ory/client-fetch';

import { handleOryCall, logger, setToken } from '@/utils';
import type { OryCallResult } from '@/utils';

interface VerifyLoginOtpResult {
  success: boolean;
  oryId?: string;
  sessionData?: OryCallResult<SuccessfulNativeLogin>;
  error?: string;
}

/**
 * Verifies an OTP for user login
 * @param ory The Ory client instance
 * @param params Login verification parameters
 * @returns Result object with success status and session data or error
 */
interface VerifyLoginOtpParams {
  flowId: string;
  csrfToken: string;
  phoneNumber: string;
  otpCode: string;
}

const verifyLoginOtp = async (
  ory: FrontendApi,
  params: VerifyLoginOtpParams,
): Promise<VerifyLoginOtpResult> => {
  const { flowId, csrfToken, phoneNumber, otpCode } = params;
  logger.debug(`Verifying login OTP for: ${phoneNumber}`);

  try {
    // Verify the OTP using our utility for handling all response status codes
    const result = await handleOryCall<SuccessfulNativeLogin>(
      () =>
        ory.updateLoginFlow({
          flow: flowId,
          updateLoginFlowBody: {
            method: 'code',
            identifier: phoneNumber,
            code: otpCode,
            csrf_token: csrfToken,
          },
        }),
      'verify login otp',
    );

    const oryId = result?.data?.session?.identity?.id;

    // Check if we have a successful authentication response
    if (oryId) {
      logger.debug(
        `Successfully authenticated user with identity ID: ${oryId}`,
      );
      await setToken(true);
      return {
        success: true,
        oryId,
        sessionData: result,
      };
    }

    throw new Error('Identity ID not found in verification result');
  } catch (error) {
    logger.error(
      `Login OTP verification error: ${error instanceof Error ? error.message : JSON.stringify(error, undefined, 2)}`,
    );
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
};

export { verifyLoginOtp };
export type { VerifyLoginOtpParams, VerifyLoginOtpResult };
